import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { IntlProvider, load, loadMessages, LocalizationProvider } from '@progress/kendo-vue-intl';
import currencyData from 'cldr-core/supplemental/currencyData.json';
import likelySubtags from 'cldr-core/supplemental/likelySubtags.json';
import weekData from 'cldr-core/supplemental/weekData.json';
import { onBeforeMount, ref, watch } from 'vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'LangWrapper',
  props: {
    language: {},
    locale: {}
  },
  setup(__props: any) {

const props = __props;

const appLanguage = ref('en');
const appLocale = ref('en-GB');

const isLocaleFilesLoading = ref(false);

async function loadLocaleFiles() {
  appLocale.value = props.locale;
  appLanguage.value = props.language;

  await loadLocaleFilesByLocale();
  await loadMessagesByLocale();
  isLocaleFilesLoading.value = true;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function overrideEnglishDateFormats(caGregorian: any, locale: string) {
  // Only override date formats for English locales (en, en-US, en-GB, etc.)
  // All other languages will use their predefined CLDR 'd' format patterns
  if (locale.toLowerCase().startsWith('en')) {
    // CLDR keys are case-sensitive, so we need to check both the original locale
    // and find the actual key in the CLDR data structure
    const localeLower = locale.toLowerCase();
    const baseLocaleKey = localeLower.split('-')[0]; // 'en' for 'en-US' or 'en-GB'

    // Helper function to override date formats for a given locale key
    const overrideFormats = (localeKey: string) => {
      // Use 'dd / MM / yyyy' format (numeric months) to ensure it can parse dates from backend
      // like "16/12/2025" while displaying with spaces around slashes
      const customFormat = 'dd/MMM/yyyy';

      // Check main structure
      if (caGregorian?.main?.[localeKey]?.dates?.calendars?.gregorian?.dateFormats) {
        caGregorian.main[localeKey].dates.calendars.gregorian.dateFormats.short = customFormat;
        caGregorian.main[localeKey].dates.calendars.gregorian.dateFormats.medium = customFormat;
      }

      // Check default.main structure (if it exists)
      if (caGregorian?.default?.main?.[localeKey]?.dates?.calendars?.gregorian?.dateFormats) {
        caGregorian.default.main[localeKey].dates.calendars.gregorian.dateFormats.short =
          customFormat;
        caGregorian.default.main[localeKey].dates.calendars.gregorian.dateFormats.medium =
          customFormat;
      }
    };

    // Try to find the actual locale key in the CLDR data
    // CLDR keys might be case-sensitive (e.g., "en-GB" vs "en-gb")
    if (caGregorian?.main) {
      // First, try the exact locale as provided
      const exactKey = Object.keys(caGregorian.main).find(
        (key) => key.toLowerCase() === localeLower,
      );
      if (exactKey) {
        overrideFormats(exactKey);
      }

      // Also try the base locale (e.g., "en")
      const baseKey = Object.keys(caGregorian.main).find(
        (key) => key.toLowerCase() === baseLocaleKey,
      );
      if (baseKey && baseKey !== exactKey) {
        overrideFormats(baseKey);
      }
    }
  }
  // Note: Non-English locales are not modified and will use their default CLDR formats
}

async function loadLocaleFilesFor(localeFolder: string, targetLocale?: string) {
  const numbers = await import(`cldr-numbers-full/main/${localeFolder}/numbers.json`);
  const caGregorian = await import(`cldr-dates-full/main/${localeFolder}/ca-gregorian.json`);
  const dateFields = await import(`cldr-dates-full/main/${localeFolder}/dateFields.json`);
  const timeZoneNames = await import(`cldr-dates-full/main/${localeFolder}/timeZoneNames.json`);
  const currencies = await import(`cldr-numbers-full/main/${localeFolder}/currencies.json`);

  // Override date formats only for English locales
  // Other languages will keep their predefined CLDR 'd' format patterns
  // Use targetLocale if provided, otherwise use localeFolder
  const localeToCheck = targetLocale || localeFolder;
  overrideEnglishDateFormats(caGregorian, localeToCheck);

  return { numbers, caGregorian, dateFields, timeZoneNames, currencies };
}

async function loadLocaleFilesByLocale() {
  try {
    const { numbers, caGregorian, dateFields, timeZoneNames, currencies } =
      await loadLocaleFilesFor(props.locale, props.locale);

    load(
      likelySubtags,
      currencyData,
      weekData,
      numbers,
      caGregorian,
      dateFields,
      timeZoneNames,
      currencies,
    );
  } catch (e) {
    try {
      console.warn('Language file by locale is missing! Second attempt with short lang name', e);
      const localeFolder = props.locale.split('-')[0];
      const { numbers, caGregorian, dateFields, timeZoneNames, currencies } =
        await loadLocaleFilesFor(localeFolder, props.locale);

      load(
        likelySubtags,
        currencyData,
        weekData,
        numbers,
        caGregorian,
        dateFields,
        timeZoneNames,
        currencies,
      );
    } catch (e) {
      console.error('Language file by locale is missing! Start trying to load it by language.', e);
      appLocale.value = props.language;
      loadLocaleFilesByLanguage();
    }
  }
}

async function loadLocaleFilesByLanguage() {
  try {
    const { numbers, caGregorian, dateFields, timeZoneNames, currencies } =
      await loadLocaleFilesFor(props.locale, props.locale);

    load(
      likelySubtags,
      currencyData,
      weekData,
      numbers,
      caGregorian,
      dateFields,
      timeZoneNames,
      currencies,
    );
  } catch (e) {
    console.warn('Language file by language code is missing!', e);
  }
}

async function loadMessagesByLocale() {
  try {
    const messages = await import(`@/assets/messages/${props.locale}/${props.locale}.json`);
    loadMessages(messages?.default, props.language);
  } catch (e) {
    console.warn('Messages file by locale is missing! Start trying to load it by language.', e);
    await loadMessagesByLanguage();
  }
}

async function loadMessagesByLanguage() {
  try {
    const messages = await import(`@/assets/messages/${props.language}/${props.language}.json`);
    loadMessages(messages?.default, props.language);
  } catch (e) {
    console.error('Messages file by language missing!', e);
  }
}

onBeforeMount(async () => {
  await loadLocaleFiles();
});

watch(
  () => props.language,
  async () => {
    await loadLocaleFiles();
  },
);

return (_ctx: any,_cache: any) => {
  return (isLocaleFilesLoading.value)
    ? (_openBlock(), _createBlock(_unref(LocalizationProvider), {
        key: 0,
        language: appLanguage.value,
        class: "localization-provider"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(IntlProvider), {
            locale: appLocale.value,
            class: "intl-provider"
          }, {
            default: _withCtx(() => [
              _renderSlot(_ctx.$slots, "default")
            ]),
            _: 3
          }, 8, ["locale"])
        ]),
        _: 3
      }, 8, ["language"]))
    : _createCommentVNode("", true)
}
}

})